import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2, Heart, MapPin, Search, Plus, X, ShieldCheck, ArrowLeft,
  Sparkles, FileDown, Megaphone, HandCoins, Inbox, Coins, Trophy, Mail,
  Compass,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FindPayCheckout from "@/components/FindPayCheckout";
import { useIdempotentPost } from "@/hooks/useIdempotentPost";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

/**
 * /charity — location-aware Pay-from-Heart donation directory.
 *   + 80G tax-exempt PDF receipt on every donation (India)
 *   + Charity Impact Feed — updates from cause owners to past donors
 *   + Match & Multiply — corporate sponsor pledges that top-up every donation
 */
export default function Charity() {
  const { user } = useAuth();
  const [cats, setCats]         = useState([]);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState("");
  const [city, setCity]         = useState("");
  const [category, setCategory] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [donate, setDonate]     = useState(null);
  const [detail, setDetail]     = useState(null);   // cause detail modal (updates + matches)
  const [tab, setTab]           = useState("causes"); // causes | inbox | mine

  useSeo({ title: "Charity · Pay from Heart · find2near",
           description: "Donate to nearby Gaushala, shelters, orphanages, food kitchens & more. One tap, secure Stripe checkout with 80G tax-exempt receipts." });

  useEffect(() => { api.get("/charity/categories").then(r => setCats(r.data)).catch(() => {}); }, []);
  useStripeReturn(user);
  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (city) params.city = city;
      if (category) params.category = category;
      const r = await api.get("/charity/causes", { params });
      setRows(r.data);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, city, category]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50" data-testid="charity-page">
      <div className="relative bg-gradient-to-br from-rose-600 to-red-800 text-white overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 py-8 relative">
          <Link to="/apps" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Back</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Heart size={12}/> Pay from Heart</div>
          <h1 className="heading text-3xl md:text-5xl font-black mt-1">Charity & Donate</h1>
          <p className="text-sm text-white/80 mt-1 max-w-md">Give to nearby causes with one tap. Every donation → an 80G tax-exempt receipt. Corporate sponsors 2× your gift when Match &amp; Multiply is active.</p>
        </div>
      </div>

      {user && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className="inline-flex flex-wrap rounded-full bg-white border border-rose-200 p-1 text-xs font-black">
            {[
              { k: "causes",  label: "Causes",       icon: Heart },
              { k: "inbox",   label: "Impact Feed",  icon: Inbox },
              { k: "mine",    label: "My donations", icon: FileDown },
              { k: "roundup", label: "Round-Up",     icon: Coins },
              { k: "top",     label: "Top Sponsors", icon: Trophy },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`rounded-full px-3 py-1.5 inline-flex items-center gap-1 ${tab === t.k ? "bg-rose-600 text-white" : "text-stone-600"}`}
                data-testid={`charity-tab-${t.k}`}>
                <t.icon size={12}/> {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {tab === "causes" && (
          <>
            <RecommendationHero onDonate={c => setDonate(c)} onOpen={c => setDetail(c)}/>

            {/* Categories */}
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {cats.map(c => (
                <button key={c.key}
                  onClick={() => setCategory(cat => cat === c.key ? null : c.key)}
                  className={`rounded-2xl p-2 text-center border-2 ${category === c.key ? "border-rose-600 bg-rose-50" : "border-stone-200 bg-white hover:border-rose-300"}`}
                  data-testid={`charity-cat-${c.key}`}>
                  <div className="text-2xl">{c.emoji}</div>
                  <div className="text-[10px] font-black uppercase mt-0.5 truncate">{c.label}</div>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search cause…" className="pl-8 rounded-full" data-testid="charity-search"/>
              </div>
              <div className="relative w-40">
                <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input value={city} onChange={e => setCity(e.target.value)} placeholder="City" className="pl-8 rounded-full" data-testid="charity-city"/>
              </div>
              {user && (
                <Button onClick={() => setShowAdd(true)} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black" data-testid="charity-add-btn">
                  <Plus size={13} className="mr-1"/> List a cause
                </Button>
              )}
            </div>

            {loading ? (
              <div className="p-8 text-center"><Loader2 className="inline animate-spin text-rose-400"/></div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center text-sm text-stone-500">
                No causes yet in this category. {user ? "Be the first — click 'List a cause'." : "Sign in to list one."}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {rows.map(c => (
                  <CauseCard key={c.cause_id} cause={c} cats={cats}
                             onOpen={() => setDetail(c)}
                             onDonate={() => setDonate(c)}/>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "inbox"   && <ImpactInbox/>}
        {tab === "mine"    && <MyDonations/>}
        {tab === "roundup" && <RoundupSettings/>}
        {tab === "top"     && <SponsorLeaderboard/>}
      </div>

      {showAdd && <AddCauseModal cats={cats} onClose={saved => { setShowAdd(false); if (saved) load(); }}/>}
      {donate && <DonateModal cause={donate} onClose={() => setDonate(null)}/>}
      {detail && <CauseDetailModal cause={detail} isOwner={user && detail.owner_user_id === user.user_id}
                                    onClose={() => setDetail(null)}
                                    onDonate={() => { setDonate(detail); setDetail(null); }}/>}
    </div>
  );
}

// SEC-S5: after Stripe returns, confirm the payment server-side before any "received" / receipt message.
function useStripeReturn(user) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("donation_cancelled");
    if (!user || (!sid && !cancelled)) return;
    if (cancelled) {
      api.post(`/charity/donations/${cancelled}/cancel`).catch(() => {})
        .finally(() => { toast.info("Payment cancelled. You were not charged."); setParams({}, { replace: true }); });
      return;
    }
    let n = 0;
    const iv = setInterval(async () => {
      n++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          clearInterval(iv); setParams({}, { replace: true });
          toast.success(params.get("verify") ? "Verified badge activated" : "Donation received. Your 80G receipt is in My donations.");
        } else if (n >= 15 || ["expired", "failed", "cancelled"].includes(r.data.payment_status)) {
          clearInterval(iv); toast.info("Payment not confirmed yet. Nothing has been charged as received.");
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
}

function CauseCard({ cause: c, cats, onOpen, onDonate }) {
  const [match, setMatch] = useState(null);
  useEffect(() => {
    api.get(`/charity/causes/${c.cause_id}/matches`).then(r => {
      const remaining = (r.data || []).reduce((s, m) => s + (m.cap_remaining_paise || 0), 0);
      const topRatio  = (r.data || []).reduce((m, x) => Math.max(m, x.match_ratio || 0), 0);
      if (remaining > 0) setMatch({ remaining, ratio: topRatio, sponsors: r.data.length });
    }).catch(() => {});
  }, [c.cause_id]);
  return (
    <div className={`bg-white rounded-3xl border-2 overflow-hidden ${c.verified ? "border-emerald-300" : "border-stone-200"}`} data-testid={`cause-${c.cause_id}`}>
      {c.photo_url && <img src={c.photo_url} alt="" className="w-full h-36 object-cover cursor-pointer" onClick={onOpen}/>}
      <div className="p-3 space-y-1">
        <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={onOpen}>
          <div className="font-black text-stone-900 flex items-center gap-1">
            {c.name}
            {c.verified && <ShieldCheck size={13} className="text-emerald-600" data-testid={`cause-verified-${c.cause_id}`}/>}
          </div>
          <div className="text-[10px] uppercase tracking-widest font-black text-rose-600 shrink-0">
            {cats.find(k => k.key === c.category)?.emoji} {cats.find(k => k.key === c.category)?.label}
          </div>
        </div>
        {c.city && <div className="text-[11px] text-stone-500 flex items-center gap-1"><MapPin size={11}/> {c.city}{c.distance_km != null && ` · ${c.distance_km} km`}</div>}
        {c.story && <p className="text-xs text-stone-600 line-clamp-2">{c.story}</p>}
        {match && (
          <div className="text-[11px] font-black text-fuchsia-700 bg-fuchsia-50 rounded-full inline-flex items-center gap-1 px-2 py-0.5" data-testid={`cause-match-${c.cause_id}`}>
            <Sparkles size={11}/> {match.ratio}× match up to {rupees(match.remaining)}
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          <div className="text-[10px] text-stone-500 flex-1">
            Raised {rupees(c.raised_paise)} {c.goal_inr ? `of ₹${c.goal_inr.toLocaleString("en-IN")}` : ""} · {c.donors_count || 0} donors
          </div>
          <Button onClick={onDonate} size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-black h-7" data-testid={`cause-donate-${c.cause_id}`}>
            <Heart size={11} className="mr-1 fill-white"/> Donate
          </Button>
        </div>
        {c.goal_inr && c.goal_inr > 0 && (
          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-rose-500 to-amber-500" style={{ width: `${Math.min(100, (c.raised_paise / 100 / c.goal_inr) * 100)}%` }}/>
          </div>
        )}
      </div>
    </div>
  );
}

function AddCauseModal({ cats, onClose }) {
  const [f, setF] = useState({ name: "", category: cats[0]?.key || "charity", city: "", story: "", photo_url: "", phone: "", website: "", goal_inr: 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/charity/causes", { ...f, goal_inr: parseInt(f.goal_inr) || null });
      toast.success("Cause listed");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3 max-h-[92vh] overflow-y-auto" data-testid="cause-create-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">List a cause</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="Cause name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="rounded-full text-sm" data-testid="cause-name"/>
        <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="w-full rounded-full text-sm border border-stone-200 px-3 py-2" data-testid="cause-category">
          {cats.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
        </select>
        <Input placeholder="City" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="rounded-full text-sm"/>
        <Input placeholder="Photo URL" value={f.photo_url} onChange={e => setF({ ...f, photo_url: e.target.value })} className="rounded-full text-sm"/>
        <Input placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="rounded-full text-sm"/>
        <Input type="number" placeholder="Fundraising goal (INR, optional)" value={f.goal_inr || ""} onChange={e => setF({ ...f, goal_inr: e.target.value })} className="rounded-full text-sm"/>
        <Textarea rows={3} placeholder="Tell us your story" value={f.story} onChange={e => setF({ ...f, story: e.target.value })} className="rounded-2xl text-sm"/>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-rose-600 hover:bg-rose-700 font-black h-10" data-testid="cause-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Plus size={14} className="mr-1"/>} List cause
        </Button>
      </div>
    </div>
  );
}

function DonateModal({ cause, onClose }) {
  const suggested = cause.suggested_amounts_inr || [51, 101, 501, 1001, 2100];
  const [amount, setAmount] = useState(suggested[1] || 101);
  const [custom, setCustom] = useState("");
  const [anon, setAnon]     = useState(false);
  const [msg, setMsg]       = useState("");
  const [pan, setPan]       = useState("");
  const [addr, setAddr]     = useState("");
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(null); // {donation_id, matched_paise, amount_inr}
  const postOnce = useIdempotentPost("don");
  const [fpOpen, setFpOpen] = useState(false);
  const finalAmt = custom ? Math.max(1, parseInt(custom) || 0) : amount;

  const donate = async () => {
    if (finalAmt < 1) { toast.error("Enter an amount"); return; }
    if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(pan.trim())) {
      toast.error("PAN must be in format ABCDE1234F"); return;
    }
    setBusy(true);
    try {
      const r = await postOnce(`/charity/causes/${cause.cause_id}/donate`, {
        amount_inr: finalAmt,
        origin_url: window.location.origin,
        anonymous:  anon,
        message:    msg || null,
        pan:        pan.trim().toUpperCase() || null,
        donor_address: addr || null,
      });
      setDone({ donation_id: r.data.donation_id,
                amount_inr:  r.data.amount_inr,
                matched_paise: r.data.matched_paise || 0,
                checkout_url: r.data.checkout_url });
    } catch (e) { toast.error(e?.response?.data?.detail || "Donation failed"); }
    finally { setBusy(false); }
  };



  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-sm space-y-3 max-h-[92vh] overflow-y-auto" data-testid="donate-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">{done ? "Thank you 🙏" : `Donate to ${cause.name}`}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>

        {done ? (
          <div className="space-y-3">
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="donate-pending">
              <div className="font-black">₹{done.amount_inr.toLocaleString("en-IN")} · payment pending</div>
              <div className="text-[11px] mt-1">Your donation counts (and sponsors match it) once the payment is confirmed. The 80G receipt is then available in My donations.</div>
              <div className="text-[11px] mt-1">Reference: {done.donation_id}</div>
            </div>
            {done.checkout_url && (
              <a href={done.checkout_url} className="block text-center rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black h-11 leading-[2.75rem]" data-testid="donate-stripe-link">
                Complete payment →
              </a>
            )}
            <p className="text-[10px] text-stone-500">Donations to {process.env.REACT_APP_TRUST_NAME || "Find2Near Foundation Trust"} are eligible for deduction under Section 80G of the Income Tax Act, 1961.</p>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-stone-500">Raised {rupees(cause.raised_paise)} · {cause.donors_count || 0} donors</div>
            <div>
              <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Suggested amounts</div>
              <div className="grid grid-cols-5 gap-1">
                {suggested.map(a => (
                  <button key={a} onClick={() => { setAmount(a); setCustom(""); }}
                    className={`rounded-full text-xs font-black py-1.5 ${amount === a && !custom ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-800 hover:bg-rose-100"}`}
                    data-testid={`donate-amt-${a}`}>
                    ₹{a}
                  </button>
                ))}
              </div>
            </div>
            <Input type="number" placeholder="Or enter custom ₹ amount" value={custom} onChange={e => setCustom(e.target.value)} className="rounded-full text-sm" data-testid="donate-custom"/>
            <Textarea rows={2} placeholder="Add a message (optional)" value={msg} onChange={e => setMsg(e.target.value)} className="rounded-2xl text-sm" data-testid="donate-msg"/>
            <div className="rounded-2xl border-2 border-dashed border-rose-200 p-2.5 space-y-2">
              <div className="text-[10px] uppercase font-black text-rose-700">80G tax-exempt receipt (India)</div>
              <Input placeholder="PAN (e.g. ABCDE1234F)" value={pan} onChange={e => setPan(e.target.value.toUpperCase())} maxLength={10} className="rounded-full text-sm uppercase" data-testid="donate-pan"/>
              <Input placeholder="Address (optional, for receipt)" value={addr} onChange={e => setAddr(e.target.value)} className="rounded-full text-sm" data-testid="donate-addr"/>
              <div className="text-[10px] text-stone-500">You'll get a downloadable 80G-eligible PDF receipt after payment. PAN is optional.</div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} data-testid="donate-anon"/>
              Donate anonymously
            </label>
            <Button onClick={donate} disabled={busy || finalAmt < 1} className="w-full rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black h-11" data-testid="donate-submit">
              {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Heart size={14} className="mr-1 fill-white"/>} Pay ₹{finalAmt.toLocaleString("en-IN")} · From Heart
            </Button>
            <button
              onClick={() => setFpOpen(true)}
              className="w-full rounded-full border border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 font-black h-10 text-xs"
              data-testid="donate-findpay"
              disabled={finalAmt < 1}
            >
              Pay with FindPay (UPI / RuPay / Cards)
            </button>
          </>
        )}
      </div>
      {fpOpen && (
        <FindPayCheckout
          open={fpOpen}
          onOpenChange={setFpOpen}
          merchantId="fpm_platform"
          amountPaise={Math.max(1, finalAmt) * 100}
          currency="INR"
          description={`Donation · ${cause.name}`}
          context={{ kind: "charity_donation", cause_id: cause.cause_id, anonymous: anon }}
          onSuccess={async (txn) => {
            setFpOpen(false);
            try {
              const r = await api.post(`/charity/causes/${cause.cause_id}/donate`, {
                amount_inr: finalAmt,
                origin_url: window.location.origin,
                anonymous:  anon,
                message:    msg || null,
                pan:        pan.trim().toUpperCase() || null,
                donor_address: addr || null,
                findpay_txn_id: txn?.txn_id || null,
              });
              setDone({
                donation_id:   r.data.donation_id,
                amount_inr:    r.data.amount_inr,
                matched_paise: r.data.matched_paise || 0,
                checkout_url:  null,
              });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Donation recorded, but 80G receipt pending — check /donations.");
            }
          }}
        />
      )}
    </div>
  );
}

function CauseDetailModal({ cause, isOwner, onClose, onDonate }) {
  const [updates, setUpdates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showPost, setShowPost] = useState(false);
  const [showMatch, setShowMatch] = useState(false);

  const load = async () => {
    try {
      const [u, m] = await Promise.all([
        api.get(`/charity/causes/${cause.cause_id}/updates`),
        api.get(`/charity/causes/${cause.cause_id}/matches`),
      ]);
      setUpdates(u.data || []);
      setMatches(m.data || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cause.cause_id]);

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-2xl space-y-4 max-h-[92vh] overflow-y-auto" data-testid="cause-detail-modal">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-black text-stone-900 text-lg">{cause.name}</h3>
            {cause.city && <div className="text-[11px] text-stone-500">{cause.city}</div>}
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={18}/></button>
        </div>

        {cause.photo_url && <img src={cause.photo_url} alt="" className="w-full h-48 object-cover rounded-2xl"/>}
        {cause.story && <p className="text-sm text-stone-700">{cause.story}</p>}

        <div className="flex gap-2 flex-wrap">
          <Button onClick={onDonate} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black" data-testid="detail-donate-btn">
            <Heart size={13} className="mr-1 fill-white"/> Donate
          </Button>
          <Button onClick={() => setShowMatch(true)} variant="outline" className="rounded-full font-black" data-testid="detail-match-btn">
            <HandCoins size={13} className="mr-1"/> Match &amp; Multiply
          </Button>
          {isOwner && (
            <Button onClick={() => setShowPost(true)} variant="outline" className="rounded-full font-black" data-testid="detail-post-update-btn">
              <Megaphone size={13} className="mr-1"/> Post update
            </Button>
          )}
        </div>

        {/* Sponsor pledges */}
        {matches.length > 0 && (
          <div className="rounded-2xl bg-fuchsia-50 border border-fuchsia-200 p-3">
            <div className="text-[11px] uppercase font-black text-fuchsia-800 flex items-center gap-1 mb-2">
              <Sparkles size={12}/> Match &amp; Multiply pledges
            </div>
            <div className="space-y-1.5">
              {matches.map(m => (
                <div key={m.match_id} className="text-xs flex justify-between items-center" data-testid={`match-row-${m.match_id}`}>
                  <span className="font-black text-stone-800">{m.sponsor_name}</span>
                  <span className="text-fuchsia-700 font-black">{m.match_ratio}× · up to {rupees(m.cap_paise)} · {rupees(m.cap_remaining_paise)} left</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Impact updates */}
        <div>
          <div className="text-[11px] uppercase font-black text-stone-700 mb-2 flex items-center gap-1">
            <Megaphone size={12}/> Impact updates {updates.length > 0 && `(${updates.length})`}
          </div>
          {updates.length === 0 ? (
            <div className="text-xs text-stone-500 py-4 text-center bg-stone-50 rounded-2xl">
              {isOwner ? "Post your first update to keep donors engaged." : "No updates yet from this cause."}
            </div>
          ) : (
            <div className="space-y-2">
              {updates.map(u => (
                <div key={u.update_id} className="rounded-2xl bg-stone-50 p-3 border border-stone-200" data-testid={`update-${u.update_id}`}>
                  <div className="font-black text-sm text-stone-900">{u.title}</div>
                  <div className="text-[10px] text-stone-500">{u.created_at?.split("T")[0]}</div>
                  <p className="text-xs text-stone-700 mt-1 whitespace-pre-wrap">{u.body}</p>
                  {u.photo_url && <img src={u.photo_url} alt="" className="w-full max-h-48 object-cover rounded-lg mt-2"/>}
                </div>
              ))}
            </div>
          )}
        </div>

        {showPost && (
          <PostUpdateModal causeId={cause.cause_id}
                           onClose={saved => { setShowPost(false); if (saved) load(); }}/>
        )}
        {showMatch && (
          <MatchPledgeModal causeId={cause.cause_id}
                            onClose={saved => { setShowMatch(false); if (saved) load(); }}/>
        )}
      </div>
    </div>
  );
}

function PostUpdateModal({ causeId, onClose }) {
  const [f, setF] = useState({ title: "", body: "", photo_url: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim() || !f.body.trim()) { toast.error("Title and body required"); return; }
    setBusy(true);
    try {
      await api.post(`/charity/causes/${causeId}/updates`, f);
      toast.success("Update posted — donors will see it in their Impact Feed");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3" data-testid="update-post-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">Post an impact update</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="Title (e.g. 100 cows fed today!)" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="rounded-full text-sm" data-testid="update-title"/>
        <Textarea rows={5} placeholder="Share what your donors made possible…" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} className="rounded-2xl text-sm" data-testid="update-body"/>
        <Input placeholder="Photo URL (optional)" value={f.photo_url} onChange={e => setF({ ...f, photo_url: e.target.value })} className="rounded-full text-sm"/>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black h-10" data-testid="update-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Megaphone size={14} className="mr-2"/>} Publish update
        </Button>
      </div>
    </div>
  );
}

function MatchPledgeModal({ causeId, onClose }) {
  const [f, setF] = useState({ sponsor_name: "", match_ratio: 1, cap_inr: 10000 });
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const save = async () => {
    if (!f.cap_inr || f.cap_inr < 100) { toast.error("Cap must be at least ₹100"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/charity/causes/${causeId}/match`, {
        sponsor_name: f.sponsor_name || null,
        match_ratio:  parseFloat(f.match_ratio) || 1,
        cap_inr:      parseInt(f.cap_inr) || 0,
      });
      toast.success("Pledge activated — building announcement…");
      // Fetch the shareable announcement pack
      try {
        const a = await api.get(`/charity/matches/${r.data.match_id}/announcement`);
        setAnnouncement(a.data);
      } catch { onClose(true); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Pledge failed"); }
    finally { setBusy(false); }
  };
  if (announcement) {
    return <AnnouncementModal a={announcement} onClose={() => onClose(true)}/>;
  }
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3" data-testid="match-pledge-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900 flex items-center gap-2"><Sparkles size={16} className="text-fuchsia-600"/> Match &amp; Multiply</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <p className="text-xs text-stone-600">Sponsor every donation to this cause up to a cap. Your name and remaining budget show as a "2× match active" badge on the cause card — donors give more when they know their impact multiplies.</p>
        <Input placeholder="Sponsor / company name" value={f.sponsor_name} onChange={e => setF({ ...f, sponsor_name: e.target.value })} className="rounded-full text-sm" data-testid="match-name"/>
        <div>
          <div className="text-[10px] uppercase font-black text-stone-600 mb-1">Match ratio</div>
          <div className="flex gap-1">
            {[0.5, 1, 2, 3].map(r => (
              <button key={r} onClick={() => setF({ ...f, match_ratio: r })}
                className={`flex-1 rounded-full text-xs font-black py-2 ${f.match_ratio === r ? "bg-fuchsia-600 text-white" : "bg-fuchsia-50 text-fuchsia-800"}`}
                data-testid={`match-ratio-${r}`}>
                {r}× ({r < 1 ? "half" : r === 1 ? "1:1" : `${r}:1`})
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black text-stone-600 mb-1">Cap (₹)</div>
          <Input type="number" value={f.cap_inr} onChange={e => setF({ ...f, cap_inr: e.target.value })} className="rounded-full text-sm" data-testid="match-cap"/>
        </div>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black h-10" data-testid="match-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <HandCoins size={14} className="mr-2"/>} Activate pledge
        </Button>
      </div>
    </div>
  );
}

function AnnouncementModal({ a, onClose }) {
  const [tab, setTab] = useState("card");
  const copy = (txt, label) => {
    navigator.clipboard?.writeText(txt).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  };
  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(a.tweet)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const shareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + "/charity")}`;
    window.open(url, "_blank", "noopener,noreferrer");
    copy(a.linkedin, "LinkedIn post");
  };
  return (
    <div className="fixed inset-0 bg-black/80 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-lg space-y-3 max-h-[92vh] overflow-y-auto" data-testid="announcement-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900 flex items-center gap-2"><Sparkles size={18} className="text-fuchsia-600"/> Share your pledge</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={18}/></button>
        </div>
        <p className="text-xs text-stone-600">Ready-to-post social card + copy. Every share is free CSR visibility.</p>

        <div className="inline-flex rounded-full bg-stone-100 p-1 text-xs font-black">
          {[["card", "Social card"], ["tweet", "Tweet"], ["linkedin", "LinkedIn"], ["press", "Press release"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 ${tab === k ? "bg-white text-fuchsia-700 shadow" : "text-stone-500"}`} data-testid={`ann-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "card" && (
          <div className="space-y-2">
            <img src={a.social_card_url} alt="Sponsor announcement card" className="w-full rounded-2xl border border-stone-200" data-testid="announcement-card-img"/>
            <a href={a.social_card_url} download={`pledge-${a.match_id}.png`} target="_blank" rel="noreferrer" className="text-xs font-black text-fuchsia-700 hover:underline" data-testid="announcement-card-download">
              Download PNG (1200×630 — perfect for X, LinkedIn, Instagram)
            </a>
          </div>
        )}

        {tab === "tweet" && (
          <div className="space-y-2">
            <textarea readOnly rows={4} value={a.tweet}
              className="w-full text-sm rounded-2xl border border-stone-200 p-3 bg-stone-50" data-testid="announcement-tweet-text"/>
            <div className="flex gap-2">
              <Button onClick={() => copy(a.tweet, "Tweet")} variant="outline" className="rounded-full font-black" data-testid="announcement-copy-tweet">Copy</Button>
              <Button onClick={shareTwitter} className="rounded-full bg-black text-white font-black" data-testid="announcement-share-tweet">Post on X</Button>
            </div>
          </div>
        )}

        {tab === "linkedin" && (
          <div className="space-y-2">
            <textarea readOnly rows={6} value={a.linkedin}
              className="w-full text-sm rounded-2xl border border-stone-200 p-3 bg-stone-50" data-testid="announcement-linkedin-text"/>
            <div className="flex gap-2">
              <Button onClick={() => copy(a.linkedin, "LinkedIn post")} variant="outline" className="rounded-full font-black" data-testid="announcement-copy-linkedin">Copy</Button>
              <Button onClick={shareLinkedIn} className="rounded-full bg-[#0a66c2] text-white font-black" data-testid="announcement-share-linkedin">Share on LinkedIn</Button>
            </div>
          </div>
        )}

        {tab === "press" && (
          <div className="space-y-2">
            <textarea readOnly rows={10} value={a.press_release}
              className="w-full text-sm rounded-2xl border border-stone-200 p-3 bg-stone-50" data-testid="announcement-press-text"/>
            <Button onClick={() => copy(a.press_release, "Press release")} variant="outline" className="rounded-full font-black" data-testid="announcement-copy-press">Copy press release</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ImpactInbox() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/charity/updates/inbox").then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  const emailDigest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/charity/digest/send-me");
      if (r.data.sent) {
        toast.success(`Digest sent — ${r.data.updates_count} update${r.data.updates_count !== 1 ? 's' : ''} across ${r.data.causes_count} cause${r.data.causes_count !== 1 ? 's' : ''}.`);
      } else {
        toast.info(`No email sent · ${r.data.reason?.replaceAll("_", " ") || "nothing new"}.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest failed"); }
    finally { setBusy(false); }
  };
  if (rows === null) return <div className="p-8 text-center"><Loader2 className="inline animate-spin text-rose-400"/></div>;
  return (
    <div className="space-y-3" data-testid="impact-inbox">
      <div className="bg-white border border-rose-200 rounded-3xl p-4 flex items-center gap-3">
        <Mail size={22} className="text-rose-600"/>
        <div className="flex-1">
          <div className="font-black text-sm text-stone-900">Weekly impact digest</div>
          <div className="text-[11px] text-stone-500">Get a single email summarising every new update from causes you support.</div>
        </div>
        <Button onClick={emailDigest} disabled={busy} size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black text-[11px] h-8" data-testid="impact-email-digest">
          {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <Mail size={12} className="mr-1"/>} Email me now
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="p-12 text-center text-sm text-stone-500" data-testid="impact-inbox-empty">
          No impact updates yet. Donate to a cause and its owner's updates will appear here.
        </div>
      ) : rows.map(u => (
        <div key={u.update_id} className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`inbox-update-${u.update_id}`}>
          <div className="text-[10px] uppercase font-black text-rose-700">{u.cause_name}</div>
          <div className="font-black text-stone-900">{u.title}</div>
          <div className="text-[10px] text-stone-500">{u.created_at?.split("T")[0]}</div>
          <p className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{u.body}</p>
          {u.photo_url && <img src={u.photo_url} alt="" className="w-full max-h-56 object-cover rounded-2xl mt-2"/>}
        </div>
      ))}
    </div>
  );
}

function MyDonations() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/charity/donations/mine").then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  const download = async (id) => {
    try {
      const r = await api.get(`/charity/donations/${id}/receipt-80g.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `80G-receipt-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Receipt download failed"); }
  };
  if (rows === null) return <div className="p-8 text-center"><Loader2 className="inline animate-spin text-rose-400"/></div>;
  if (rows.length === 0) return (
    <div className="p-12 text-center text-sm text-stone-500">No donations yet.</div>
  );
  return (
    <div className="space-y-2" data-testid="my-donations">
      {rows.map(d => (
        <div key={d.donation_id} className="bg-white rounded-2xl border border-stone-200 p-3 flex items-center gap-3" data-testid={`donation-${d.donation_id}`}>
          <div className="flex-1">
            <div className="font-black text-sm text-stone-900">{d.cause_name}</div>
            <div className="text-[10px] text-stone-500">{d.created_at?.split("T")[0]} · {d.donation_id}</div>
            {d.matched_paise > 0 && (
              <div className="text-[10px] font-black text-fuchsia-700">Match added: {rupees(d.matched_paise)}</div>
            )}
          </div>
          <div className="text-lg font-black text-rose-700">{rupees(d.amount_paise)}</div>
          {d.payment_status === "paid" ? (
            <Button size="sm" onClick={() => download(d.donation_id)} className="rounded-full bg-stone-900 text-white font-black h-8 text-[11px]" data-testid={`donation-receipt-${d.donation_id}`}>
              <FileDown size={12} className="mr-1"/> 80G
            </Button>
          ) : (
            <span className="text-[10px] font-black uppercase text-amber-700" data-testid={`donation-status-${d.donation_id}`}>{d.payment_status}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function RoundupSettings() {
  const [s, setS]   = useState(null);
  const [causes, setCauses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [simAmt, setSimAmt] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/charity/roundup/settings"),
        api.get("/charity/causes", { params: { limit: 50 } }),
      ]);
      setS(a.data);
      setCauses(b.data);
    } catch { setS({ enabled: false, bucket: 10, default_cause_id: null, monthly_cap_inr: 500 }); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const body = { ...(s || {}), ...patch };
      const r = await api.put("/charity/roundup/settings", {
        enabled: !!body.enabled,
        bucket: parseInt(body.bucket) || 10,
        default_cause_id: body.default_cause_id || null,
        monthly_cap_inr: parseInt(body.monthly_cap_inr) || 0,
      });
      setS({ ...s, ...r.data });
      toast.success("Round-up saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const simulate = async () => {
    if (!parseInt(simAmt)) { toast.error("Enter a checkout amount"); return; }
    setSimBusy(true); setSimResult(null);
    try {
      const r = await api.post("/charity/roundup/apply", {
        original_amount_inr: parseInt(simAmt),
        origin_url: window.location.origin,
      });
      setSimResult(r.data);
      toast.success(`₹${r.data.roundup_inr} round-up pledged to ${r.data.cause_name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Round-up failed"); }
    finally { setSimBusy(false); }
  };

  if (!s) return <div className="p-8 text-center"><Loader2 className="inline animate-spin text-rose-400"/></div>;

  const bucket = s.bucket || 10;
  return (
    <div className="space-y-3" data-testid="roundup-settings">
      <div className="rounded-3xl bg-gradient-to-br from-amber-100 to-rose-100 border border-amber-200 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Coins size={22} className="text-amber-600"/>
          <div className="flex-1">
            <div className="font-black text-stone-900">Auto Round-Up · From Heart</div>
            <div className="text-[11px] text-stone-700">Round every purchase up to the nearest ₹10 / ₹50 / ₹100 and donate the change to your chosen cause.</div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!s.enabled} onChange={e => save({ enabled: e.target.checked })} data-testid="roundup-toggle"/>
            <span className="text-xs font-black">{s.enabled ? "ON" : "OFF"}</span>
          </label>
        </div>

        <div>
          <div className="text-[10px] uppercase font-black text-stone-600 mb-1">Round to nearest</div>
          <div className="flex gap-1">
            {[10, 50, 100].map(b => (
              <button key={b} onClick={() => save({ bucket: b })} disabled={busy}
                className={`flex-1 rounded-full text-xs font-black py-2 ${bucket === b ? "bg-amber-600 text-white" : "bg-white text-amber-800"}`}
                data-testid={`roundup-bucket-${b}`}>₹{b}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase font-black text-stone-600 mb-1">Default cause</div>
          <select
            value={s.default_cause_id || ""}
            onChange={e => save({ default_cause_id: e.target.value || null })}
            className="w-full rounded-full text-sm border border-stone-200 px-3 py-2 bg-white"
            data-testid="roundup-default-cause">
            <option value="">Pick a cause…</option>
            {causes.map(c => <option key={c.cause_id} value={c.cause_id}>{c.name} · {c.city || ""}</option>)}
          </select>
        </div>

        <div>
          <div className="text-[10px] uppercase font-black text-stone-600 mb-1">Monthly cap (₹)</div>
          <Input type="number" value={s.monthly_cap_inr ?? 500}
                 onChange={e => setS({ ...s, monthly_cap_inr: e.target.value })}
                 onBlur={() => save({ monthly_cap_inr: parseInt(s.monthly_cap_inr) || 0 })}
                 className="rounded-full text-sm bg-white" data-testid="roundup-cap"/>
          <div className="text-[10px] text-stone-500 mt-1">Donated this month: ₹{(s.donated_this_month_inr || 0).toLocaleString("en-IN")}</div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border-2 border-dashed border-stone-300 p-4 space-y-2">
        <div className="text-[10px] uppercase font-black text-stone-600 flex items-center gap-1"><Sparkles size={11}/> Try it — simulate any checkout</div>
        <div className="flex gap-2">
          <Input type="number" placeholder={`e.g. 247 → round-up to next ₹${bucket}`}
                 value={simAmt} onChange={e => setSimAmt(e.target.value)}
                 className="rounded-full text-sm" data-testid="roundup-sim-amt"/>
          <Button onClick={simulate} disabled={simBusy || !s.enabled || !s.default_cause_id}
                  className="rounded-full bg-stone-900 text-white font-black h-9" data-testid="roundup-sim-run">
            {simBusy ? <Loader2 size={13} className="animate-spin"/> : "Round up & donate"}
          </Button>
        </div>
        {(!s.enabled || !s.default_cause_id) && (
          <div className="text-[10px] text-rose-700">Turn round-up ON and pick a default cause first.</div>
        )}
        {simResult && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="roundup-sim-result">
            <div className="font-black text-emerald-900">+₹{simResult.roundup_inr} donated to {simResult.cause_name}</div>
            {simResult.matched_paise > 0 && (
              <div className="text-[11px] text-fuchsia-700 font-black">✨ Match &amp; Multiply added {rupees(simResult.matched_paise)} on top!</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SponsorLeaderboard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/charity/sponsors/leaderboard", { params: { limit: 25 } })
       .then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  const rankStyle = (i) => i === 0 ? "from-amber-400 to-yellow-600" :
                          i === 1 ? "from-slate-300 to-slate-500" :
                          i === 2 ? "from-orange-500 to-amber-800" :
                                    "from-stone-300 to-stone-500";
  if (rows === null) return <div className="p-8 text-center"><Loader2 className="inline animate-spin text-rose-400"/></div>;
  return (
    <div className="space-y-3" data-testid="sponsor-leaderboard">
      <div className="rounded-3xl bg-gradient-to-br from-fuchsia-600 to-purple-800 text-white p-4">
        <div className="flex items-center gap-2">
          <Trophy size={22}/>
          <div className="flex-1">
            <div className="font-black">Top Match &amp; Multiply sponsors</div>
            <div className="text-[11px] text-white/80">Every rupee matched wins visibility. Brands, this is your leaderboard.</div>
          </div>
          <Link to="/sponsors/dashboard" className="rounded-full bg-white text-fuchsia-800 font-black text-[11px] px-3 py-1.5 hover:bg-fuchsia-50" data-testid="sponsors-cta">
            My CSR dashboard →
          </Link>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-12 text-center text-sm text-stone-500" data-testid="sponsors-empty">
          No sponsors on the board yet. Become the first — pledge a Match &amp; Multiply on any cause!
        </div>
      ) : rows.map((r, i) => (
        <div key={`${r.sponsor_user_id}-${i}`} className="bg-white rounded-2xl border border-stone-200 p-3 flex items-center gap-3" data-testid={`sponsor-row-${i}`}>
          <div className={`rounded-full w-10 h-10 grid place-items-center bg-gradient-to-br ${rankStyle(i)} text-white font-black`}>#{i + 1}</div>
          <div className="flex-1">
            <div className="font-black text-stone-900 text-sm">{r.sponsor_name || "Anonymous sponsor"}</div>
            <div className="text-[10px] text-stone-500">
              {r.causes_supported_count} cause{r.causes_supported_count !== 1 ? "s" : ""} ·
              {" "}{r.active_pledges} active pledge{r.active_pledges !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-black text-fuchsia-700">{rupees(r.total_matched_paise)}</div>
            <div className="text-[9px] uppercase font-black text-stone-500">matched</div>
          </div>
        </div>
      ))}
    </div>
  );
}


function RecommendationHero({ onDonate, onOpen }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    // Try to use user's location for a local signal; fall back silently.
    const fire = (params) => {
      api.get("/charity/recommendations", { params }).then(r => setData(r.data))
                                                     .catch(() => setData({ recommendations: [] }))
                                                     .finally(() => setLoading(false));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p => fire({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => fire({}),
        { timeout: 3000, maximumAge: 300_000 },
      );
    } else fire({});
  }, [user]);

  if (!user) return null;
  if (loading) return <div className="p-3 text-center"><Loader2 className="inline animate-spin text-rose-400 h-4 w-4"/></div>;
  if (!data?.featured) return null;

  const f = data.featured;
  const others = (data.recommendations || []).slice(1, 4);
  return (
    <div className="space-y-2" data-testid="charity-recommendations">
      <div className="rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 text-white p-4 relative overflow-hidden" data-testid="cause-of-the-week">
        <div className="text-[10px] uppercase tracking-widest font-black text-white/90 flex items-center gap-1"><Sparkles size={11}/> Cause of the week · for you</div>
        <div className="flex items-start gap-3 mt-2">
          {f.photo_url && <img src={f.photo_url} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-white/30"/>}
          <div className="flex-1">
            <div className="text-lg font-black leading-tight">{f.name}</div>
            {(f.reasons || []).slice(0, 2).map((r, i) => (
              <div key={i} className="text-[11px] text-white/90 mt-0.5 flex items-center gap-1">
                <Compass size={10}/> {r}
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={() => onDonate(f)} className="rounded-full bg-white text-rose-700 hover:bg-rose-50 font-black text-[11px] h-8" data-testid="cotw-donate">
            <Heart size={11} className="mr-1 fill-rose-600"/> Donate now
          </Button>
          <Button onClick={() => onOpen(f)} variant="outline" className="rounded-full border-white/60 text-white hover:bg-white/10 font-black text-[11px] h-8 bg-white/5" data-testid="cotw-open">
            View cause
          </Button>
        </div>
      </div>
      {others.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="charity-rec-list">
          {others.map(c => (
            <button key={c.cause_id} onClick={() => onOpen(c)}
                    className="shrink-0 min-w-[180px] max-w-[220px] bg-white rounded-2xl border border-rose-200 p-2 text-left hover:border-rose-400"
                    data-testid={`cause-rec-${c.cause_id}`}>
              <div className="text-[10px] uppercase font-black text-rose-600">{c.category?.replace(/_/g, " ")}</div>
              <div className="font-black text-sm text-stone-900 truncate">{c.name}</div>
              {(c.reasons || [])[0] && <div className="text-[10px] text-stone-500 truncate">{c.reasons[0]}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

